import { FC, useMemo, useCallback } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';
import { useDeleteUser } from 'Pages/SettingsPage/UsersListPage/hooks/useDeleteUser';
import DeleteUserButton from './DeleteUserButton';

const DeleteUserConfirmation: FC = () => {
  const { deleteUser, isDeleting, isDeletingOwnAccount } = useDeleteUser();
  const { Modal, modalProps, openModal, closeModal } = useModal();

  const toggleModal = useCallback(() => {
    if (modalProps.isOpen) {
      closeModal();
    } else {
      openModal(modalProps);
    }
  }, [closeModal, openModal, modalProps]);

  const handleDeleteUser = useCallback(() => {
    deleteUser();
    closeModal();
  }, [deleteUser, closeModal]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('DELETE'),
        variant: 'primary',
        color: 'danger',
        onClick: handleDeleteUser,
        loading: isDeleting,
        disabled: isDeleting,
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: toggleModal,
      },
    ],
    [handleDeleteUser, toggleModal, isDeleting]
  );

  return (
    <>
      {!isDeletingOwnAccount && <DeleteUserButton toggleModal={toggleModal} />}
      <Modal
        {...modalProps}
        title={`${intl.get('SETTINGS_PAGE.USER_PAGE.DELETE_USER.TITLE')}?`}
        titleIcon={{
          name: 'trash',
          className: 'text-error-dark',
        }}
        aria-label={`${intl.get('SETTINGS_PAGE.USER_PAGE.DELETE_USER.TITLE')}?`}
        size='small'
        closeModal={closeModal}
        actionButtons={actionButtons}
      >
        <Typography variant='body'>
          {intl.getHTML('SETTINGS_PAGE.USER_PAGE.DELETE_USER.BODY')}
        </Typography>
      </Modal>
    </>
  );
};

export default DeleteUserConfirmation;
